import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import { api, errorMessage } from "../../lib/api";
import { ArrowLeft, ArrowRight, Plus, Trash, DotsSixVertical } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reorder, useDragControls } from "framer-motion";

const Q_TYPES = [
    { v: "single_choice", l: "Single choice" },
    { v: "multi_choice", l: "Multiple choice" },
    { v: "yes_no", l: "Yes / No" },
    { v: "rating", l: "Rating (1-N)" },
    { v: "text", l: "Short text" },
    { v: "long_text", l: "Long text" },
    { v: "ranking", l: "Ranking" },
    { v: "matrix", l: "Matrix" },
];

const newQ = (i) => ({
    id: `q${Date.now()}_${i}`,
    type: "single_choice",
    text: "",
    options: ["Option A", "Option B"],
    required: true,
    is_attention_check: false,
    expected_answer: "",
    branch_rules: [],
    matrix_rows: [],
});

export default function SurveyBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [catalog, setCatalog] = useState(null);
    const [s, setS] = useState({
        title: "",
        description: "",
        category: "",
        questions: [newQ(1)],
        targeting: { countries: ["USA", "Canada"], states: [], age_min: null, age_max: null,
                     genders: [], income_ranges: [], employment: [], education: [], interests: [] },
        targeted: false,
        responses_needed: 100,
        estimated_minutes: 5,
        quotas: [],
    });
    const [econ, setEcon] = useState(null);
    const [serverSurvey, setServerSurvey] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        api.get("/catalog/demographics").then((r) => setCatalog(r.data));
    }, []);

    useEffect(() => {
        if (id) {
            api.get(`/surveys/${id}`).then((r) => {
                setS({
                    title: r.data.title, description: r.data.description, category: r.data.category,
                    questions: r.data.questions, targeting: r.data.targeting, targeted: r.data.targeted,
                    responses_needed: r.data.responses_needed, estimated_minutes: r.data.estimated_minutes,
                    quotas: r.data.quotas || [],
                });
                setServerSurvey(r.data);
            });
        }
    }, [id]);

    useEffect(() => {
        api.post("/surveys/cost-preview", {
            num_questions: s.questions.length,
            responses_needed: s.responses_needed,
            targeted: s.targeted,
        }).then((r) => setEcon(r.data));
    }, [s.questions.length, s.responses_needed, s.targeted]);

    const setQ = (idx, patch) => setS((cur) => ({ ...cur, questions: cur.questions.map((q, i) => i === idx ? { ...q, ...patch } : q) }));
    const addQ = () => setS((cur) => ({ ...cur, questions: [...cur.questions, newQ(cur.questions.length + 1)] }));
    const removeQ = (qid) => setS((cur) => ({ ...cur, questions: cur.questions.filter((q) => q.id !== qid) }));
    const reorderQs = (next) => setS((cur) => ({ ...cur, questions: next }));

    const save = async () => {
        setSubmitting(true);
        try {
            let saved;
            if (serverSurvey) {
                const r = await api.put(`/surveys/${serverSurvey.id}`, s);
                saved = r.data;
                toast.success("Saved");
            } else {
                const r = await api.post("/surveys", s);
                saved = r.data;
                toast.success("Draft saved");
                navigate(`/advertiser/surveys/${saved.id}/edit`, { replace: true });
            }
            setServerSurvey(saved);
            return saved;
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setSubmitting(false);
        }
    };

    const payAndSubmit = async () => {
        const saved = await save();
        if (!saved) return;
        try {
            // Real Stripe Checkout (test mode). Backend computes the dollar
            // amount from saved.advertiser_cost_usd; we just hand off the
            // origin so success/cancel redirects come back to this app.
            const r = await api.post(`/payments/checkout/survey/${saved.id}`, {
                origin_url: window.location.origin,
            });
            if (!r.data?.url) throw new Error("Checkout URL missing");
            window.location.href = r.data.url;
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    if (!catalog) return <div className="min-h-screen flex items-center justify-center text-xs font-mono">LOADING…</div>;

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
                <button onClick={() => navigate(-1)} className="btn btn-ghost px-2 mb-6" data-testid="builder-back-btn">
                    <ArrowLeft size={16} /> Back
                </button>
                <p className="eyebrow mb-2 text-advertiser">{serverSurvey ? "EDIT SURVEY" : "NEW SURVEY"}</p>
                <h1 className="h2 mb-8" data-testid="builder-title">Survey builder</h1>

                <div className="grid lg:grid-cols-3 gap-8">
                    {/* main column */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="card-base p-6 space-y-4">
                            <p className="eyebrow">SETUP</p>
                            <input className="input-base" placeholder="Survey title" value={s.title} onChange={(e) => setS({ ...s, title: e.target.value })} data-testid="builder-title-input" />
                            <textarea className="input-base" placeholder="Description shown to respondents" value={s.description} onChange={(e) => setS({ ...s, description: e.target.value })} data-testid="builder-desc-input" />
                            <div className="grid sm:grid-cols-3 gap-3">
                                <select className="input-base" value={s.category} onChange={(e) => setS({ ...s, category: e.target.value })} data-testid="builder-category-select">
                                    <option value="">Category…</option>
                                    {catalog.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <input type="number" min={1} className="input-base" placeholder="Responses needed" value={s.responses_needed} onChange={(e) => setS({ ...s, responses_needed: Number(e.target.value) })} data-testid="builder-responses-input" />
                                <input type="number" min={1} className="input-base" placeholder="Est. minutes" value={s.estimated_minutes} onChange={(e) => setS({ ...s, estimated_minutes: Number(e.target.value) })} data-testid="builder-minutes-input" />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <p className="eyebrow">QUESTIONS · {s.questions.length} <span className="text-ink-600 normal-case tracking-normal">— drag to reorder</span></p>
                            <Reorder.Group axis="y" values={s.questions} onReorder={reorderQs} className="space-y-4">
                                {s.questions.map((q, idx) => (
                                    <QuestionEditor
                                        key={q.id}
                                        idx={idx}
                                        q={q}
                                        setQ={(p) => setQ(idx, p)}
                                        remove={() => removeQ(q.id)}
                                        all={s.questions}
                                    />
                                ))}
                            </Reorder.Group>
                            <button type="button" onClick={addQ} className="btn btn-outline w-full" data-testid="builder-add-q-btn">
                                <Plus size={16} /> Add question
                            </button>
                        </div>

                        <QuotasPanel s={s} setS={setS} catalog={catalog} />

                        <LogicPreview questions={s.questions} />

                        <div className="card-base p-6">
                            <button type="button" onClick={() => setShowAdvanced((v) => !v)} className="text-sm underline" data-testid="builder-advanced-toggle">
                                {showAdvanced ? "− Hide" : "+ Show"} advanced targeting
                            </button>
                            {showAdvanced && (
                                <div className="mt-6 space-y-4">
                                    <label className="flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={s.targeted} onChange={(e) => setS({ ...s, targeted: e.target.checked })} data-testid="builder-targeted-check" />
                                        Use demographic targeting (×2 cost)
                                    </label>
                                    <MultiSelect label="Genders" value={s.targeting.genders} options={catalog.genders} onChange={(v) => setS({ ...s, targeting: { ...s.targeting, genders: v } })} />
                                    <MultiSelect label="Income ranges" value={s.targeting.income_ranges} options={catalog.income_ranges} onChange={(v) => setS({ ...s, targeting: { ...s.targeting, income_ranges: v } })} />
                                    <MultiSelect label="Employment" value={s.targeting.employment} options={catalog.employment} onChange={(v) => setS({ ...s, targeting: { ...s.targeting, employment: v } })} />
                                    <MultiSelect label="Education" value={s.targeting.education} options={catalog.education} onChange={(v) => setS({ ...s, targeting: { ...s.targeting, education: v } })} />
                                    <MultiSelect label="Interests" value={s.targeting.interests} options={catalog.interests} onChange={(v) => setS({ ...s, targeting: { ...s.targeting, interests: v } })} />
                                    <div className="grid grid-cols-2 gap-3">
                                        <input type="number" placeholder="Min age" className="input-base" value={s.targeting.age_min || ""} onChange={(e) => setS({ ...s, targeting: { ...s.targeting, age_min: Number(e.target.value) || null } })} data-testid="targ-age-min" />
                                        <input type="number" placeholder="Max age" className="input-base" value={s.targeting.age_max || ""} onChange={(e) => setS({ ...s, targeting: { ...s.targeting, age_max: Number(e.target.value) || null } })} data-testid="targ-age-max" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* sidebar */}
                    <aside className="space-y-4">
                        <div className="card-base p-6 sticky top-20">
                            <p className="eyebrow mb-3">COST CALCULATOR</p>
                            {econ && (
                                <>
                                    <div className="flex justify-between py-2 text-sm border-b border-ink-200"><span>Per response</span><span className="font-mono">{econ.points_per_response} pts (${(econ.points_per_response/100).toFixed(2)})</span></div>
                                    <div className="flex justify-between py-2 text-sm border-b border-ink-200"><span>Responses</span><span className="font-mono">×{econ.responses_needed}</span></div>
                                    <div className="flex justify-between py-2 text-sm border-b border-ink-200"><span>Respondent payout</span><span className="font-mono">${econ.respondent_payout_usd}</span></div>
                                    <div className="flex justify-between py-2 text-sm border-b border-ink-200"><span>Platform fee (20%)</span><span className="font-mono">${econ.platform_revenue_usd}</span></div>
                                    <div className="flex justify-between py-3 text-base font-bold"><span>Total</span><span className="font-mono text-advertiser" data-testid="builder-total-cost">${econ.advertiser_cost_usd}</span></div>
                                </>
                            )}
                            <div className="space-y-2 mt-4">
                                <button onClick={save} disabled={submitting} className="btn btn-outline w-full text-sm" data-testid="builder-save-draft-btn">
                                    {submitting ? "Saving…" : "Save draft"}
                                </button>
                                <button onClick={payAndSubmit} disabled={submitting || !s.title || s.questions.length === 0} className="btn btn-advertiser w-full" data-testid="builder-submit-btn">
                                    Pay & submit for review <ArrowRight size={16} />
                                </button>
                                <p className="text-[10px] text-ink-600 font-mono">STRIPE CHECKOUT (MOCKED)</p>
                            </div>
                        </div>
                    </aside>
                </div>
            </main>
            <Footer />
        </div>
    );
}

function QuestionEditor({ idx, q, setQ, remove, all }) {
    const dragControls = useDragControls();
    return (
        <Reorder.Item value={q} dragListener={false} dragControls={dragControls} className="card-base p-5" data-testid={`builder-q-${idx}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onPointerDown={(e) => dragControls.start(e)}
                        className="cursor-grab active:cursor-grabbing text-ink-600 hover:text-ink touch-none p-1"
                        aria-label="Drag to reorder"
                        data-testid={`q-${idx}-drag`}
                    >
                        <DotsSixVertical size={20} weight="bold" />
                    </button>
                    <span className="font-mono text-xs text-ink-600">Q{idx + 1}</span>
                </div>
                <button onClick={remove} className="btn btn-ghost p-1 text-error" data-testid={`q-${idx}-del`}><Trash size={16} /></button>
            </div>
            <input className="input-base mb-3" placeholder="Question text" value={q.text} onChange={(e) => setQ({ text: e.target.value })} data-testid={`q-${idx}-text`} />
            <div className="grid grid-cols-2 gap-3 mb-3">
                <select className="input-base" value={q.type} onChange={(e) => setQ({ type: e.target.value })} data-testid={`q-${idx}-type`}>
                    {Q_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm px-2">
                    <input type="checkbox" checked={q.required} onChange={(e) => setQ({ required: e.target.checked })} /> Required
                </label>
            </div>
            {(q.type === "single_choice" || q.type === "multi_choice" || q.type === "ranking" || q.type === "matrix") && (
                <div className="mb-3">
                    <div className="text-xs mb-1 eyebrow">{q.type === "matrix" ? "COLUMNS" : "OPTIONS"} (one per line)</div>
                    <textarea
                        className="input-base text-sm"
                        rows={Math.max(3, q.options.length)}
                        value={(q.options || []).join("\n")}
                        onChange={(e) => setQ({ options: e.target.value.split("\n").filter(Boolean) })}
                        data-testid={`q-${idx}-options`}
                    />
                </div>
            )}
            {q.type === "matrix" && (
                <div className="mb-3">
                    <div className="text-xs mb-1 eyebrow">ROWS (one per line)</div>
                    <textarea className="input-base text-sm" rows={Math.max(3, q.matrix_rows.length)}
                        value={(q.matrix_rows || []).join("\n")}
                        onChange={(e) => setQ({ matrix_rows: e.target.value.split("\n").filter(Boolean) })} />
                </div>
            )}
            <div className="border-t border-ink-200 pt-3 space-y-2">
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={q.is_attention_check} onChange={(e) => setQ({ is_attention_check: e.target.checked })} data-testid={`q-${idx}-attn`} />
                    Use as attention check
                </label>
                {q.is_attention_check && (
                    <input className="input-base text-sm" placeholder="Expected answer" value={q.expected_answer || ""} onChange={(e) => setQ({ expected_answer: e.target.value })} data-testid={`q-${idx}-expected`} />
                )}
                {(q.type === "single_choice" || q.type === "yes_no" || q.type === "rating") && (
                    <div>
                        <div className="text-xs eyebrow mt-2">BRANCH LOGIC</div>
                        {(q.branch_rules || []).map((r, ri) => (
                            <div key={ri} className="grid grid-cols-12 gap-2 my-2 items-center" data-testid={`q-${idx}-rule-${ri}`}>
                                <span className="col-span-2 text-xs">If answer =</span>
                                <input className="input-base col-span-3 text-sm" value={r.when_value} onChange={(e) => {
                                    const rs = [...q.branch_rules]; rs[ri] = { ...r, when_value: e.target.value }; setQ({ branch_rules: rs });
                                }} data-testid={`q-${idx}-rule-${ri}-when`} />
                                <select className="input-base col-span-3 text-sm" value={r.action} onChange={(e) => {
                                    const rs = [...q.branch_rules]; rs[ri] = { ...r, action: e.target.value }; setQ({ branch_rules: rs });
                                }} data-testid={`q-${idx}-rule-${ri}-action`}>
                                    <option value="skip_to">skip to</option>
                                    <option value="disqualify">disqualify</option>
                                    <option value="end">end survey (paid)</option>
                                </select>
                                {r.action === "skip_to" && (
                                    <select className="input-base col-span-3 text-sm" value={r.target_question_id || ""} onChange={(e) => {
                                        const rs = [...q.branch_rules]; rs[ri] = { ...r, target_question_id: e.target.value }; setQ({ branch_rules: rs });
                                    }} data-testid={`q-${idx}-rule-${ri}-target`}>
                                        <option value="">choose later question…</option>
                                        {all.filter((_, j) => j > idx).map((qq, j) => <option key={qq.id} value={qq.id}>Q{idx + 2 + j}: {(qq.text || qq.id).slice(0, 40)}</option>)}
                                    </select>
                                )}
                                {r.action === "skip_to" && !r.target_question_id && (
                                    <span className="col-span-12 text-[10px] text-error font-mono">⚠ pick a later question or this rule is ignored</span>
                                )}
                                <button className="btn btn-ghost text-error col-span-1" onClick={() => setQ({ branch_rules: q.branch_rules.filter((_, j) => j !== ri) })} data-testid={`q-${idx}-rule-${ri}-del`}><Trash size={14} /></button>
                            </div>
                        ))}
                        <button onClick={() => setQ({ branch_rules: [...(q.branch_rules || []), { when_value: "", action: "skip_to", target_question_id: "" }] })} className="btn btn-ghost text-xs" data-testid={`q-${idx}-add-rule`}>+ Add rule</button>
                    </div>
                )}
            </div>
        </Reorder.Item>
    );
}

function QuotasPanel({ s, setS, catalog }) {
    const [open, setOpen] = useState(false);
    const quotas = s.quotas || [];
    const setQuotas = (qs) => setS({ ...s, quotas: qs });
    const addQuota = () => setQuotas([
        ...quotas,
        { id: `quota_${Date.now()}`, name: "", filter: { genders: [], income_ranges: [], employment: [], education: [], interests: [], age_min: null, age_max: null, states: [], countries: [] }, max_responses: 50, current_count: 0 },
    ]);
    const remQuota = (idx) => setQuotas(quotas.filter((_, i) => i !== idx));
    const setQ = (idx, patch) => setQuotas(quotas.map((q, i) => i === idx ? { ...q, ...patch } : q));
    const setF = (idx, patch) => setQuotas(quotas.map((q, i) => i === idx ? { ...q, filter: { ...q.filter, ...patch } } : q));

    return (
        <div className="card-base p-6">
            <button type="button" onClick={() => setOpen((v) => !v)} className="text-sm underline" data-testid="builder-quotas-toggle">
                {open ? "− Hide" : "+ Show"} demographic quotas ({quotas.length})
            </button>
            {open && (
                <div className="mt-6 space-y-4">
                    <p className="text-xs text-ink-600">
                        Cap responses per demographic bucket. Example: &ldquo;Female respondents max 50&rdquo;. A respondent who matches a full bucket will not see this survey. Leave a field empty to ignore it.
                    </p>
                    {quotas.map((q, idx) => (
                        <div key={q.id} className="border border-ink-200 p-4" data-testid={`quota-${idx}`}>
                            <div className="flex items-center justify-between gap-3 mb-3">
                                <input
                                    className="input-base flex-1 text-sm"
                                    placeholder="Bucket name (e.g. Females 25-34)"
                                    value={q.name}
                                    onChange={(e) => setQ(idx, { name: e.target.value })}
                                    data-testid={`quota-${idx}-name`}
                                />
                                <button type="button" onClick={() => remQuota(idx)} className="btn btn-ghost text-error p-1" data-testid={`quota-${idx}-del`}>
                                    <Trash size={16} />
                                </button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4 mb-3">
                                <div>
                                    <div className="eyebrow mb-1">Max responses for this bucket</div>
                                    <input
                                        type="number" min={1}
                                        className="input-base text-sm"
                                        value={q.max_responses}
                                        onChange={(e) => setQ(idx, { max_responses: Number(e.target.value) || 1 })}
                                        data-testid={`quota-${idx}-max`}
                                    />
                                    <div className="font-mono text-[10px] text-ink-600 mt-1">
                                        {q.current_count || 0} / {q.max_responses} filled
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <input type="number" placeholder="Min age" className="input-base text-sm" value={q.filter.age_min || ""} onChange={(e) => setF(idx, { age_min: Number(e.target.value) || null })} data-testid={`quota-${idx}-age-min`} />
                                    <input type="number" placeholder="Max age" className="input-base text-sm" value={q.filter.age_max || ""} onChange={(e) => setF(idx, { age_max: Number(e.target.value) || null })} data-testid={`quota-${idx}-age-max`} />
                                </div>
                            </div>
                            <div className="space-y-3">
                                <MultiSelect label="Genders" value={q.filter.genders} options={catalog.genders} onChange={(v) => setF(idx, { genders: v })} />
                                <MultiSelect label="Income ranges" value={q.filter.income_ranges} options={catalog.income_ranges} onChange={(v) => setF(idx, { income_ranges: v })} />
                                <MultiSelect label="Employment" value={q.filter.employment} options={catalog.employment} onChange={(v) => setF(idx, { employment: v })} />
                                <MultiSelect label="Education" value={q.filter.education} options={catalog.education} onChange={(v) => setF(idx, { education: v })} />
                                <MultiSelect label="Interests" value={q.filter.interests} options={catalog.interests} onChange={(v) => setF(idx, { interests: v })} />
                            </div>
                        </div>
                    ))}
                    <button type="button" onClick={addQuota} className="btn btn-outline w-full text-sm" data-testid="builder-add-quota-btn">
                        <Plus size={16} /> Add quota bucket
                    </button>
                </div>
            )}
        </div>
    );
}

function LogicPreview({ questions }) {
    const titleFor = (qid) => {
        const i = questions.findIndex((x) => x.id === qid);
        if (i === -1) return "—";
        const q = questions[i];
        return `Q${i + 1}: ${(q.text || q.id).slice(0, 40)}`;
    };
    const rules = [];
    questions.forEach((q, i) => {
        (q.branch_rules || []).forEach((r, ri) => {
            const tgt = i + 1;
            const fromLabel = `Q${i + 1}`;
            let actionLabel;
            let warn = null;
            if (r.action === "disqualify") actionLabel = "DISQUALIFY (no payout)";
            else if (r.action === "end") actionLabel = "END SURVEY (paid)";
            else if (r.action === "skip_to") {
                if (!r.target_question_id) {
                    actionLabel = "SKIP TO …";
                    warn = "no target — rule will be ignored";
                } else {
                    const ti = questions.findIndex((x) => x.id === r.target_question_id);
                    if (ti === -1) {
                        actionLabel = `SKIP TO unknown`;
                        warn = "target question deleted";
                    } else if (ti <= i) {
                        actionLabel = `SKIP TO ${titleFor(r.target_question_id)}`;
                        warn = "backwards skip — server will ignore";
                    } else {
                        actionLabel = `SKIP TO ${titleFor(r.target_question_id)}`;
                    }
                }
            } else {
                actionLabel = (r.action || "?").toUpperCase();
            }
            rules.push({ key: `${q.id}-${ri}`, fromLabel, when: r.when_value, actionLabel, warn, qIdx: i, tgt });
        });
    });

    return (
        <div className="card-base p-6" data-testid="logic-preview-panel">
            <p className="eyebrow mb-3">LOGIC PREVIEW · {rules.length}</p>
            {rules.length === 0 ? (
                <p className="text-xs text-ink-600">No branching rules yet. Add per-question rules above to create skip-paths, screen-outs, or early-exits.</p>
            ) : (
                <ul className="space-y-2 text-sm" data-testid="logic-preview-list">
                    {rules.map((r) => (
                        <li key={r.key} className="font-mono text-xs flex items-start gap-3" data-testid={`logic-rule-${r.qIdx}`}>
                            <span className="text-ink-600">{r.fromLabel}</span>
                            <span className="text-ink-400">·</span>
                            <span>if answer = <strong className="text-ink">{r.when || "—"}</strong></span>
                            <span className="text-ink-400">→</span>
                            <span className={r.actionLabel.startsWith("DISQUALIFY") ? "text-error" : r.actionLabel.startsWith("END") ? "text-success" : "text-advertiser"}>
                                {r.actionLabel}
                            </span>
                            {r.warn && <span className="text-error">⚠ {r.warn}</span>}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function MultiSelect({ label, value, options, onChange }) {
    const arr = value || [];
    const toggle = (o) => {
        onChange(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    };
    return (
        <div>
            <div className="eyebrow mb-2">{label}</div>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => (
                    <button type="button" key={o} onClick={() => toggle(o)} className={`btn ${arr.includes(o) ? "btn-ink" : "btn-outline"} text-xs py-1 px-2`}>
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );
}
